import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, Grid, Typography, Button, Divider, CircularProgress } from '@mui/material';
import TemplateImage from 'layouts/projects/Forms/SheetTemplates/SheetTemplateForm/components/TemplateImage';
import {
    KanatForm,
    KanatBadges,
} from 'layouts/projects/Forms/SheetTemplates/SheetTemplateForm/components/KanatForm/index.js';
import {
    UKasaForm,
    UKasaBadges,
} from 'layouts/projects/Forms/SheetTemplates/SheetTemplateForm/components/UKasaForm/index.js';
import {
    KasaForm,
    KasaBadges,
} from 'layouts/projects/Forms/SheetTemplates/SheetTemplateForm/components/KasaForm/index.js';
import {
    PervazForm,
    PervazBadges,
} from 'layouts/projects/Forms/SheetTemplates/SheetTemplateForm/components/PervazForm/index.js';
import {
    UPervazForm,
    UPervazBadges,
} from 'layouts/projects/Forms/SheetTemplates/SheetTemplateForm/components/UPervazForm/index.js';
import { getSheetTemplate } from 'services/api/project';
import { getParameters } from 'services/api/general/parameter';
import { parseStringNumber } from 'helpers/smallCodes';
import RetrievePriceAndNames from 'layouts/projects/Forms/SheetTemplates/SheetTemplateForm/components/RetrievePriceAndNames';
import { SHEET_TYPES, SHEET_FIELDS } from './constants';

const fetchSheetTemplate = async (id, filteredDataParameters) => {
    const response = await getSheetTemplate(id, filteredDataParameters)
        .then((rp) => rp.data.result)
        .catch((err) => err);
    return response;
};

const fetchParameters = async (id) => {
    const response = await getParameters(``).then((rp) => (rp.data?.result ? rp.data?.result : []));
    return response;
};

const transformDataToInitialAndMatches = (data, dataParameters, sheetName) => {
    const initial = {};
    const matches = {};
    if (data) {
        dataParameters.forEach((param) => {
            const key = param.key.split('_').slice(1).join('_');
            const sheetKey = param.value;
            const sheetValue = data[`${sheetName}!${sheetKey}`]?.[0]?.[0];
            if (sheetValue) {
                initial[key] = parseStringNumber(sheetValue);
                matches[key] = sheetName + '!' + sheetKey;
            }
        });
    }
    return { initial, matches };
};

function SheetTemplate({ sheetTemplate, demandID, handleClose }) {
    const [lock, setLock] = useState(false);
    const queryClient = useQueryClient();
    const sheetName = sheetTemplate.name;

    const { data, isLoading, error } = useQuery({
        queryKey: ['fetchParameters'],
        queryFn: fetchParameters,
        staleTime: 600000,
    });

    const filteredDataParameters = useMemo(() => {
        if (!data) return [];

        const baseParameters = data
            .filter((el) => el?.tag?.name === sheetTemplate.type)
            .map((el) => `${sheetName}!${el.value}`);

        const additionalParameters = Object.values(SHEET_FIELDS[sheetTemplate.type] || {});
        return [...baseParameters, ...additionalParameters.map((param) => `${sheetName}!${param}`)];
    }, [data, sheetTemplate.type, sheetName]);

    const {
        data: fetchedData,
        isLoading: isLoadingData,
        error: errorData,
        refetch,
    } = useQuery({
        queryKey: ['fetchSheetTemplate', sheetTemplate._id, filteredDataParameters],
        queryFn: () => fetchSheetTemplate(sheetTemplate._id, filteredDataParameters),
        staleTime: 600000,
        enabled: !!data,
    });

    if (isLoading || isLoadingData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <CircularProgress size={40} sx={{ color: '#3ECF8E' }} />
            </Box>
        );
    }

    if (error || errorData) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="error" variant="body1">
                    Bir Hata Oluştu: {error?.message || errorData?.message}
                </Typography>
                <Button
                    variant="outlined"
                    sx={{
                        mt: 2,
                        borderColor: '#3ECF8E',
                        color: '#3ECF8E',
                        '&:hover': { borderColor: '#13B888', backgroundColor: 'rgba(62, 207, 142, 0.04)' },
                    }}
                    onClick={() => refetch()}
                >
                    Tekrar Dene
                </Button>
            </Box>
        );
    }

    const { initial, matches } = transformDataToInitialAndMatches(fetchedData?.sheetData, data, sheetName);

    const renderForm = () => {
        const formProps = {
            googleSheetURL: sheetTemplate.url,
            initial,
            matches,
            refetch,
            lock,
            setLock,
        };

        switch (sheetTemplate.type) {
            case SHEET_TYPES.KANAT:
                return <KanatForm {...formProps} />;
            case SHEET_TYPES.U_KASA:
                return <UKasaForm {...formProps} />;
            case SHEET_TYPES.KASA:
                return <KasaForm {...formProps} />;
            case SHEET_TYPES.PERVAZ:
                return <PervazForm {...formProps} />;
            case SHEET_TYPES.U_PERVAZ:
                return <UPervazForm {...formProps} />;
            default:
                return null;
        }
    };

    const renderBadges = () => {
        switch (sheetTemplate.type) {
            case SHEET_TYPES.KANAT:
                return <KanatBadges initial={initial} />;
            case SHEET_TYPES.U_KASA:
                return <UKasaBadges initial={initial} />;
            case SHEET_TYPES.KASA:
                return <KasaBadges initial={initial} />;
            case SHEET_TYPES.PERVAZ:
                return <PervazBadges initial={initial} />;
            case SHEET_TYPES.U_PERVAZ:
                return <UPervazBadges initial={initial} />;
            default:
                return null;
        }
    };

    return (
        <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
            {/* Üst Kısım - Görsel ve Form */}
            <Grid container spacing={3}>
                {/* Sol taraf - Görsel */}
                <Grid
                    size={{
                        xs: 12,
                        md: 4,
                        lg: 3,
                    }}
                >
                    <Box
                        sx={{
                            position: 'sticky',
                            top: '20px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                        }}
                    >
                        {/* Görsel */}
                        <Box
                            sx={{
                                backgroundColor: 'white',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                border: '1px solid #EEEEEE',
                                p: 2,
                            }}
                        >
                            <TemplateImage coverImage={sheetTemplate?.coverImage} sheetURL={sheetTemplate?.url} />
                        </Box>

                        {/* Şablon Bilgileri */}
                        <Box
                            sx={{
                                backgroundColor: 'white',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                border: '1px solid #EEEEEE',
                                p: 2,
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    color: '#64748B',
                                    mb: 1.5,
                                    fontWeight: 500,
                                }}
                            >
                                Şablon Bilgileri
                            </Typography>
                            {renderBadges()}
                        </Box>
                    </Box>
                </Grid>

                {/* Sağ taraf - Form ve Fiyat Seçenekleri */}
                <Grid
                    size={{
                        xs: 12,
                        md: 8,
                        lg: 9,
                    }}
                >
                    {/* Form */}
                    <Box
                        sx={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            border: '1px solid #EEEEEE',
                            p: 3,
                            mb: 3,
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                color: '#1E293B',
                                mb: 3,
                                fontWeight: 600,
                            }}
                        >
                            Ölçü Bilgileri
                        </Typography>
                        {renderForm()}
                    </Box>

                    {/* Fiyat Seçenekleri */}
                    <Box
                        sx={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            border: '1px solid #EEEEEE',
                            p: 3,
                        }}
                    >
                        <RetrievePriceAndNames
                            queryClient={queryClient}
                            handleClose={handleClose}
                            demandID={demandID}
                            templateImage={sheetTemplate.coverImage}
                            type={sheetTemplate.type}
                            sheetName={sheetName}
                            sheetData={fetchedData?.sheetData}
                            lock={lock}
                            setLock={setLock}
                        />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}

export default SheetTemplate;
